import type { FlightOptionsContainer } from '@elara/client-core/containers/FlightOptionsContainer';
import type { FlightOption, SearchGoogleFlightsInput } from '@elara/domain/models/schemas/flight-schemas';
import type { MessageRef } from '@elara/domain/models/schemas/shortlist-schemas';

import { useState } from 'react';

import { cn, pluralize } from '@elara/client-core/lib/utils';

import { useApp, useTripsState, useUIState } from '#states/hooks.js';
import { Button } from '#ui/button.js';
import { FlightActions, FlightCard } from '#ui/cards/FlightCard.js';
import { useMessageRef } from '#ui/hooks/index.js';
import { Card, IconSizes, MaterialIcon } from '#ui/index.js';

export interface FlightSearchWidgetProps {
  search?: SearchGoogleFlightsInput;
  results: FlightOption[];
  className?: string;
  messageRef?: MessageRef;
}

/**
 * FlightSearchWidget displays flight search results by loading each option from Firestore.
 * Requires a current trip; results are stored under that trip when the tool runs.
 */
export function FlightSearchWidget({
  search,
  results,
  className,
  messageRef: propMessageRef,
}: FlightSearchWidgetProps) {
  const uiState = useUIState();
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;

  const contextMessageRef = useMessageRef();
  const messageRef = contextMessageRef || propMessageRef;
  const [showAllFlights, setShowAllFlights] = useState(false);

  if (results.length === 0) {
    return (
      <Card className={cn('w-full my-4', className)}>
        <div className="p-6 text-center text-muted-foreground">
          <MaterialIcon icon="flight" size={IconSizes.lg} className="mx-auto mb-2" />
          <p>No flight options available</p>
        </div>
      </Card>
    );
  }

  if (!tripId) {
    return undefined;
  }

  const currentTripId = tripId;
  const optionsWithIds = results.filter((o): o is FlightOption & { id: string } => typeof o.id === 'string');
  const INITIAL_FLIGHT_COUNT = 3;
  const visibleOptions = showAllFlights ? optionsWithIds : optionsWithIds.slice(0, INITIAL_FLIGHT_COUNT);
  const hasMoreFlights = optionsWithIds.length > INITIAL_FLIGHT_COUNT;

  return (
    <div className={cn('@container w-full', className)}>
      <div className="flex flex-col gap-3 w-full items-start">
        {visibleOptions.map((optionData) => (
          <FlightOptionCard
            key={optionData.id}
            tripId={currentTripId}
            optionId={optionData.id}
            messageRef={messageRef}
          />
        ))}
        {hasMoreFlights && !showAllFlights && (
          <Button variant="link" size="sm" onClick={() => setShowAllFlights(true)}>
            View {optionsWithIds.length - INITIAL_FLIGHT_COUNT} more available{' '}
            {pluralize(optionsWithIds.length - INITIAL_FLIGHT_COUNT, 'flight')}
          </Button>
        )}
      </div>
    </div>
  );
}

function FlightOptionCard({
  tripId,
  optionId,
  messageRef,
}: {
  tripId: string;
  optionId: string;
  messageRef?: MessageRef;
}) {
  const tripsState = useTripsState();
  const { features } = useApp('features');
  const flightOptions: FlightOptionsContainer = (features as { flightOptions: FlightOptionsContainer }).flightOptions;
  const handle = flightOptions.ClientFlightOption.get({ tripId, id: optionId });

  if (!handle.isReady || !handle.data) {
    return <div className="h-32 w-full rounded-md animate-pulse bg-muted/50" aria-busy="true" />;
  }

  const option = handle.data;
  return (
    <FlightCard
      flightOption={option}
      actions={<FlightActions flightOption={option} messageRef={messageRef} variant="icon" />}
      size="lg"
      className="border bg-background rounded-md w-full transition-all duration-300 ease-in-out hover:shadow-md"
      onClick={() => tripsState.showGuide('flight', option.id)}
    />
  );
}
